// run-command-transport-factory.ts — P19c: the `avatar autopilot run`
// factory that ensures the mission's root post exists, then builds the
// Mattermost transport around it. Root post FIRST because
// mattermost-command-parser.ts rejects every post whose thread root !=
// `expectedRootId` — a transport built before a root post exists would poll
// forever and accept nothing ("0 caller" #11 — mission-root-post.ts).
//
// Req 2/2a (C4/C8): every return path — the branches this factory owns AND
// the ones from buildMattermostTransportDeps — calls `onBanner` exactly once
// with the Mattermost line (D8 literal when enabled, a named reason when not)
// before returning. `disabled(reason)` is the one place that happens, so
// every `return` stays a single line.
//
// H16 (2026-09-29): every LOCAL precondition (credentials → channel →
// base_url → approver → actor binding) runs BEFORE the first network call.
// Before this, a mission with credentials but `approvers: []` got
// `GET /users/me` + a real root post on the channel inviting replies that were
// then all ignored. A token the server rejects (401/403 at getMe) stops here as
// `token_rejected`, never reaching createPost; any other getMe failure stays
// fail-soft as before.
//
// X6 (2026-10-01): those local preconditions are no longer written here — they
// are the shared verdict of mattermost-transport-preconditions.ts, the same one
// the transport wiring and `autopilot doctor` ask, so the three can no longer
// drift (this file's order is the one the shared module kept).

import { formatRunBannerLines } from "./autopilot-environment-checks.js";
import { readMattermostCredentials } from "./mattermost-credentials-file.js";
import { MattermostApiError, MattermostRestClient } from "./mattermost-rest-client.js";
import { buildRootPostSenderWithMarker } from "./mattermost-root-post-sender-with-marker.js";
import {
  evaluateMattermostMissionPreconditions,
  MATTERMOST_DISABLED_REASONS,
  type MattermostDisabledReason,
} from "./mattermost-transport-preconditions.js";
import { readMissionActorBinding } from "./mission-actor-binding.js";
import { ensureMissionRootPost } from "./mission-root-post.js";
import { buildMattermostTransportDeps } from "./run-command-mattermost-transport-wiring.js";
import type { AutopilotConfig } from "./schemas/config-schema.js";
import type { MattermostTransportDeps } from "./supervisor-transport-tick.js";

export interface BuildRunCommandTransportFactoryInput {
  workspaceRoot: string;
  controlRoot: string;
  missionId: string;
  /** Called with the banner line(s) exactly once per factory invocation, on EVERY return path (Req 2). */
  onBanner?: (line: string) => void;
  /** Parent env to scan for filtered keys (Req 2) — defaults to `process.env`, injectable for tests. */
  env?: NodeJS.ProcessEnv;
}

/** Returns the factory `runMissionToIdle` calls once `config` is resolved. Every missing precondition degrades to `undefined` (local-only), never an exception. */
export function buildRunCommandTransportFactory(
  input: BuildRunCommandTransportFactoryInput,
): (config: AutopilotConfig) => Promise<MattermostTransportDeps | undefined> {
  return async (config: AutopilotConfig) => {
    const onBanner = input.onBanner;
    const env = input.env ?? process.env;
    const disabled = (reason: MattermostDisabledReason): undefined => {
      const [line] = formatRunBannerLines({ mattermost: { enabled: false, reason }, env });
      if (line !== undefined) onBanner?.(line);
      return undefined;
    };

    // Local facts only — the shared verdict decides, before any network call.
    const verdict = evaluateMattermostMissionPreconditions({
      config,
      credentials: await readMattermostCredentials(input.workspaceRoot),
      binding: await readMissionActorBinding(input.controlRoot),
    });
    if (!verdict.enabled) return disabled(verdict.reason);
    const { botToken, channelId, baseUrl, missionCreatedAtMs } = verdict;

    const client = new MattermostRestClient({
      baseUrl,
      botToken,
      timeoutMs: config.network_timeout_sec * 1000,
    });

    // Identify the bot so its own posts are never parsed as directives. A
    // rejected token (401/403) disables the transport before any POST; any
    // other failure is not fatal (the `boundUserId` check still rejects the
    // bot's posts).
    let botUserId = "";
    try {
      botUserId = (await client.getMe()).id;
    } catch (err) {
      if (err instanceof MattermostApiError && err.classification.retryClass === "auth_failure") {
        return disabled(MATTERMOST_DISABLED_REASONS.token_rejected);
      }
    }

    // X1: a stable correlation id + marker so a lost response is reconciled
    // on the next run instead of posting a second root.
    const correlationId = `root-post-${input.missionId}`;
    const rootPost = await ensureMissionRootPost({
      controlRoot: input.controlRoot,
      missionId: input.missionId,
      channelId,
      message:
        `Mission \`${input.missionId}\` đang chạy. Trả lời trong thread này bằng ` +
        "`@avatar <lệnh>` (vd `@avatar approve g3 <request-id>`).",
      correlationId,
      sender: buildRootPostSenderWithMarker({
        client,
        channelId,
        correlationId,
        sinceMs: missionCreatedAtMs,
        botUserId,
      }),
    });

    // A root post that could not be confirmed leaves the mission local-only
    // for this run rather than binding it to an id that may not exist —
    // every later reply would otherwise be rejected as `wrong_thread`.
    if (rootPost.rootPostId === undefined) {
      return disabled(MATTERMOST_DISABLED_REASONS.root_post_unconfirmed);
    }

    const outcome = await buildMattermostTransportDeps({
      // Reuse this client rather than letting the deps-builder construct a
      // second one against the same origin/token (review follow-up).
      client,
      workspaceRoot: input.workspaceRoot,
      controlRoot: input.controlRoot,
      missionId: input.missionId,
      config,
      missionCreatedAtMs,
      rootPostId: rootPost.rootPostId,
      botUserId,
    });

    if (!outcome.enabled) return disabled(outcome.reason);
    const [enabledLine] = formatRunBannerLines({
      mattermost: { enabled: true, channelId: outcome.channelId },
      env,
    });
    if (enabledLine !== undefined) onBanner?.(enabledLine);
    return outcome.deps;
  };
}
